import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { api, UnauthorizedError } from "@/lib/api";
import type { LicenseStatus, UpdateCheckResponse } from "@/lib/types";
import { useAuth } from "./useAuth";

const POLL_INTERVAL_MS = 5 * 60 * 1000;
const DISMISS_KEY = "leif:dismissed-update";

export type SystemHealth = {
  license: LicenseStatus | null;
  update: UpdateCheckResponse | null;
  /** "ok" wenn der letzte Poll erfolgreich war, sonst "lost" */
  connection: "ok" | "lost";
  /** Welche Update-Version hat der User zuletzt weggeklickt? */
  dismissedUpdate: string | null;
  dismissUpdate: (version: string) => void;
  refresh: () => void;
};

const Ctx = createContext<SystemHealth | null>(null);

function getDismissed(): string | null {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(DISMISS_KEY);
}

export function SystemHealthProvider({ children }: { children: ReactNode }) {
  const { token } = useAuth();
  const [license, setLicense] = useState<LicenseStatus | null>(null);
  const [update, setUpdate] = useState<UpdateCheckResponse | null>(null);
  const [connection, setConnection] = useState<"ok" | "lost">("ok");
  const [dismissedUpdate, setDismissed] = useState<string | null>(getDismissed);
  const timerRef = useRef<number | null>(null);

  const refresh = useCallback(async () => {
    if (!token) return;
    let anyOk = false;
    let anyFail = false;
    try {
      const lic = await api.get<LicenseStatus>("/api/license");
      setLicense(lic);
      anyOk = true;
    } catch (e) {
      if (e instanceof UnauthorizedError) return; // Logout läuft separat
      anyFail = true;
    }
    try {
      const upd = await api.get<UpdateCheckResponse>("/api/update/check");
      setUpdate(upd);
      anyOk = true;
    } catch (e) {
      if (e instanceof UnauthorizedError) return;
      anyFail = true;
    }
    // "lost" nur wenn beide Polls fehlschlugen — ein Endpoint allein
    // (z.B. Updater nicht konfiguriert) soll keinen Connection-Banner triggern
    if (anyOk) setConnection("ok");
    else if (anyFail) setConnection("lost");
  }, [token]);

  useEffect(() => {
    if (!token) {
      setLicense(null);
      setUpdate(null);
      setConnection("ok");
      return;
    }
    void refresh();
    timerRef.current = window.setInterval(() => void refresh(), POLL_INTERVAL_MS);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
      timerRef.current = null;
    };
  }, [token, refresh]);

  const dismissUpdate = useCallback((version: string) => {
    window.localStorage.setItem(DISMISS_KEY, version);
    setDismissed(version);
  }, []);

  const value = useMemo<SystemHealth>(
    () => ({ license, update, connection, dismissedUpdate, dismissUpdate, refresh: () => void refresh() }),
    [license, update, connection, dismissedUpdate, dismissUpdate, refresh],
  );

  return createElement(Ctx.Provider, { value }, children);
}

export function useSystemHealth(): SystemHealth {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useSystemHealth muss innerhalb von <SystemHealthProvider> stehen");
  return ctx;
}
